import { useCallback, useState } from "react";
import { useGaConsentContext } from "./provider";
import type { FullGAConsentSettings } from "./types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

type CookiePrefs = Record<
  "functional" | "analytics" | "advertisement",
  boolean
>;

const defaultConsentSettings: FullGAConsentSettings = {
  ad_personalization: "granted",
  ad_user_data: "granted",
  ad_storage: "granted",
  ads_data_redaction: "true",

  analytics_storage: "granted",
  security_storage: "granted",
  personalization_storage: "granted",
  functionality_storage: "granted",
};

export function CookieConsentModal() {
  const [consentSettings, dispatchContextSettings] = useGaConsentContext();
  const [openCookieModal, setOpenCookieModal] = useState(
    consentSettings === null,
  );

  const [openCustomizeCookiesModal, setOpenCustomizeCookiesModal] =
    useState(false);

  const savePreferences = useCallback(
    (prefs: CookiePrefs) => {
      const newConsentSettings: FullGAConsentSettings = {
        ad_personalization: prefs.advertisement ? "granted" : "denied",
        ad_user_data: prefs.advertisement ? "granted" : "denied",
        ad_storage: prefs.advertisement ? "granted" : "denied",
        ads_data_redaction: "true",

        analytics_storage: prefs.analytics ? "granted" : "denied",
        security_storage: "granted",
        personalization_storage: "denied",
        functionality_storage: prefs.functional ? "granted" : "denied",
      };

      dispatchContextSettings({ verb: "update", value: newConsentSettings });
    },
    [dispatchContextSettings, consentSettings],
  );

  const [cookiePrefs, setCookiePrefs] = useState({
    functional: false,
    analytics: false,
    advertisement: false,
  });

  return (
    <>
      {/* Cookie Modal */}
      <Dialog open={openCookieModal} onOpenChange={setOpenCookieModal}>
        <DialogContent>
          <DialogTitle>We value your privacy</DialogTitle>
          <DialogDescription>
            We use cookies to enhance your browsing experience, serve
            personalised ads or content, and analyse our traffic. By clicking
            "Accept All", you consent to our use of cookies.
          </DialogDescription>
          <div className="mt-4 flex flex-col gap-2">
            <Button
              className="w-full bg-pink-500 text-white hover:bg-pink-600"
              onClick={() => {
                dispatchContextSettings({
                  verb: "update",
                  value: defaultConsentSettings,
                });
                setOpenCookieModal(false);
              }}
            >
              Accept
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setOpenCookieModal(false);
                setOpenCustomizeCookiesModal(true);
              }}
            >
              Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Customize Cookies Modal */}
      <Dialog
        open={openCustomizeCookiesModal}
        onOpenChange={setOpenCustomizeCookiesModal}
      >
        <DialogContent className="max-h-[80vh] w-full max-w-screen-xl overflow-y-auto max-md:max-w-[350px]">
          <DialogTitle className="mb-2 text-2xl font-bold">
            Customize cookies
          </DialogTitle>
          <div className="mb-6">
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Necessary</span>
                <span className="font-medium text-pink-500">Always on</span>
              </div>
              <Switch checked disabled />
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Necessary cookies are required to enable the basic features of
              this site, such as providing secure log-in or adjusting your
              consent preferences. These cookies do not store any personally
              identifiable data.
            </p>
            <div className="mb-1 flex items-center justify-between">
              <span className="font-semibold">Functional</span>
              <Switch
                checked={cookiePrefs.functional}
                onCheckedChange={() =>
                  setCookiePrefs((p) => ({ ...p, functional: !p.functional }))
                }
              />
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Functional cookies help perform certain functionalities like
              sharing the content of the website on social media platforms,
              collecting feedback, and other third-party features.
            </p>
            <div className="mb-1 flex items-center justify-between">
              <span className="font-semibold">Analytics</span>
              <Switch
                checked={cookiePrefs.analytics}
                onCheckedChange={() =>
                  setCookiePrefs((p) => ({ ...p, analytics: !p.analytics }))
                }
              />
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Analytical cookies are used to understand how visitors interact
              with the website. These cookies help provide information on
              metrics such as the number of visitors, bounce rate, traffic
              source, etc.
            </p>
            <div className="mb-1 flex items-center justify-between">
              <span className="font-semibold">Advertisement</span>
              <Switch
                checked={cookiePrefs.advertisement}
                onCheckedChange={() =>
                  setCookiePrefs((p) => ({
                    ...p,
                    advertisement: !p.advertisement,
                  }))
                }
              />
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Advertisement cookies are used to provide visitors with customised
              advertisements based on the pages you visited previously and to
              analyse the effectiveness of the ad campaigns.
            </p>
          </div>
          <Button
            className="w-full bg-pink-500 py-6 text-lg text-white hover:bg-pink-600"
            onClick={() => {
              savePreferences(cookiePrefs);
              setOpenCustomizeCookiesModal(false);
            }}
          >
            Save Preferences
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
